import { AsyncPipe, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { getDownloadURL, ref, Storage } from '@angular/fire/storage'
import { ReactiveFormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import {
	MatDialog,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatError } from '@angular/material/select'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Order, OrderStatus, ShoeComponentTableItem, ShoeComponentTableRow } from 'domain/order.model'
import { AppUser, UserVO } from 'domain/user/app-user.model'
import { concatMap, defer, firstValueFrom, forkJoin, from, Observable, of } from 'rxjs'
import { bufferCount, map, switchMap, take, tap } from 'rxjs/operators'
import { OrderPathParams, OrdersFirestoreService } from 'src/app/core/firestore-services/orders-firestore.service'
import { ShopService } from 'src/app/core/services/shop.service'
import { AuthService } from 'src/app/modules/auth/auth.service'
import { ShopSettingsService } from 'src/app/modules/auth/shop-settings.service'
import { SellerOrderConfirmationDialog } from 'src/app/shared/shopping-cart/seller-order-confirmation-dialog/seller-order-confirmation-dialog.component'
import { CartService } from 'src/app/modules/dashboard/shop/services/cart.service'
import { StripeService } from 'src/app/modules/dashboard/shop/services/stripe.service'
import { LoadingService } from '../../core/services/loading.service'
import { AbstractFormDialog } from '../dialog/abstract-form-dialog'
import { FirestoreService } from '../modules/fire/firestore.service'
import { FmxControlSubmitDirective } from '../modules/form/submit/control-submit.directive'
import { FmxFormSubmitDirective } from '../modules/form/submit/form-submit.directive'
import { CartOrderFormComponent } from './cart-order-form/cart-order-form.component'
import { CartOrder } from '../../modules/dashboard/shop/services/cart-order.type'
import { assertNonNullish } from '../../../../commons/assertions'

@Component({
	selector: 'app-shopping-cart-dialog',
	template: ` <div class="flex-row-wrap flex-align-space-between-center">
		<div class="flex-row-nowrap flex-align-space-between-center" style="margin-bottom: 0.5rem; width: 100%">
			<h2 mat-dialog-title style="margin: 0">{{ 'obiect.shoppingCart' | translate }}</h2>
			<button
				mat-dialog-close
				mat-button
				class="dialog-exit-button"
				(click)="close()"
				style="color:rgba(0, 0, 0, 0.25); min-width: 2rem !important; padding: 0 !important"
			>
				<mat-icon>cancel</mat-icon>
			</button>
		</div>

		<form [formGroup]="form" (fmxSubmit)="onSubmit()" class="flex-100">
			<mat-dialog-content>
				@if (cartService.hasItems()) {
					<app-cart-order-form formControlName="model" (conditionsAgree)="agreed = $event" />
				} @else {
					<p>{{ 'dialog.emptyCart' | translate }}</p>
				}

				@if (errors$ | async; as errors) {
					<mat-error class="mb-3">{{ errors }}</mat-error>
				}
			</mat-dialog-content>

			<div mat-dialog-actions>
				<button mat-button mat-dialog-close class="btn-custom-default" (click)="close()">
					{{ 'actiune.inchide' | translate }}
				</button>

				@if (cartService.hasItems()) {
					<button
						mat-raised-button
						type="submit"
						[ngClass]="agreed ? 'btn-custom-primary' : 'btn-custom-primary-disabled'"
						color="primary"
						[disabled]="!agreed"
					>
						<ng-container>{{ 'actiune.comanda' | translate }}</ng-container>
						<mat-icon>arrow_right_alt</mat-icon>
					</button>
				}
			</div>
		</form>
	</div>`,
	styles: [
		`
			:host {
				.flex-row-wrap {
					display: flex;
					flex-direction: row;
					flex-wrap: wrap;
				}

				.flex-row-nowrap {
					display: flex;
					flex-direction: row;
					flex-wrap: nowrap;
				}

				.flex-align-space-between-center {
					justify-content: space-between;
					align-items: center;
				}

				.flex-100 {
					flex: 0 0 100%;
					max-width: 100%;
				}
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatDialogTitle,
		MatButton,
		MatDialogClose,
		MatIcon,
		ReactiveFormsModule,
		FmxFormSubmitDirective,
		MatDialogContent,
		CartOrderFormComponent,
		FmxControlSubmitDirective,
		MatError,
		MatDialogActions,
		NgClass,
		AsyncPipe,
		TranslatePipe,
	],
})
export class ShoppingCartDialog extends AbstractFormDialog<CartOrder, ShoppingCartDialog> {
	readonly loadingService: LoadingService
	private readonly storage = inject(Storage)
	readonly cartService = inject(CartService)
	private readonly shopSettingsService = inject(ShopSettingsService)
	private readonly dialog = inject<MatDialog>(MatDialog)
	private readonly ordersService = inject<FirestoreService<Order, OrderPathParams>>(OrdersFirestoreService)
	private readonly translateService = inject<TranslateService>(TranslateService)
	readonly stripeService = inject<StripeService>(StripeService)
	readonly shopService = inject<ShopService>(ShopService)
	protected readonly dialogRef: MatDialogRef<ShoppingCartDialog>
	protected readonly authService = inject<AuthService>(AuthService)

	agreed: boolean = false
	images: Array<Observable<string>> = []

	constructor() {
		const dialogRef = inject<MatDialogRef<ShoppingCartDialog>>(MatDialogRef)
		const loadingService = inject<LoadingService>(LoadingService)

		super(dialogRef, loadingService)

		this.dialogRef = dialogRef
		this.loadingService = loadingService
	}

	onSubmit(): void {
		if (this.shopService.isPublicShop()) {
			super.onSubmit()
		} else {
			this.confirmOrderByLoggedInUser().then((confirmed) => {
				if (confirmed) {
					super.onSubmit()
				}
			})
		}
	}

	close(): void {}

	protected initialFormValue(): CartOrder {
		const currentOrder = this.cartService.currentOrder()
		assertNonNullish(currentOrder, 'Cart order is null or undefined')
		return currentOrder
	}

	/**
	 * When an order is submitted we need data from the active user, the current shop and the cart.
	 * In order to send correct emails, we translate the shoe components to the current language (on FE).
	 * Having modified the order, we initiate checkout depending on the shop type.
	 *
	 * @protected
	 */
	protected action$(cartOrder: CartOrder): Observable<unknown> {
		const shopSettings = this.shopSettingsService.shopSettings()
		assertNonNullish(shopSettings, 'Shop settings are null or undefined')

		const newOrder = this.createOrder(cartOrder)

		newOrder.amount = this.cartService.totalItemsAmount()
		newOrder.buyerPrice = this.cartService.totalBuyerPrice()
		newOrder.adminPrice = this.cartService.totalAdminPrice()
		newOrder.currency = shopSettings.currency

		const appUser = this.authService.appUser()
		newOrder.pay_seller = appUser ? AppUser.toUserVO(appUser) : undefined

		newOrder.lang = this.translateService.currentLang

		let notes = ''
		newOrder.items.forEach((item) => {
			if (item.description) {
				notes += item.shoeName + ' - ' + item.description + ' '
			}
		})
		newOrder.notes = notes

		// todo: ixaria-migration - [implement] - translate summary and names

		if (this.shopService.isPublicShop()) {
			return defer(async () => {
				const session = await this.stripeService.checkout(newOrder)
				return this.stripeService.redirectToCheckout(session.id)
			})
		}

		newOrder.status = OrderStatus.New
		return defer(() => this.ordersService.add(newOrder, { userUid: shopSettings.uid })).pipe(
			tap(() => this.cartService.clearCart()),
		)

		// const cartTotalAmount = this.cartService.totalItemsAmount()
		// const cartTotalBuyerPrice = this.cartService.totalBuyerPrice()
		// const cartTotalAdminPrice = this.cartService.totalAdminPrice()
		//
		// return forkJoin([this.authService.appUser$ as Observable<AppUser>, this.cartService.orderCart$.pipe(take(1))]).pipe(
		// 	map(([appUser, constructedOrder]) => {
		// 		cartOrder.amount = cartTotalAmount
		// 		cartOrder.value_sell = cartTotalBuyerPrice
		// 		cartOrder.value = cartTotalAdminPrice
		// 		cartOrder.pay_seller = appUser ? AppUser.toUserVO(appUser) : undefined
		// 		cartOrder.lang = this.translateService.currentLang
		// 		cartOrder.uid = constructedOrder.uid
		// 		cartOrder.shoes = constructedOrder.shoes
		// 		cartOrder.notes = ''
		// 		let summaries: Record<string, { name: string; summary: ShoeComponentTableItem[] }> = {}
		// 		cartOrder.shoes.forEach((shoeOrder) => {
		// 			if (shoeOrder.description) {
		// 				cartOrder.notes += shoeOrder.shoe.name + ' - ' + shoeOrder.description + ' '
		// 			}
		// 			summaries[shoeOrder.id] = {
		// 				name: shoeOrder.shoe.displayName,
		// 				summary: OrderUtils.computeSummary(shoeOrder.shoe),
		// 			}
		// 		})
		// 		return summaries
		// 	}),
		// 	switchMap((summaries) => {
		// 		// initiate translations and wait for them to finish
		// 		let summaryTranslations: Array<Observable<any>> = []
		// 		Object.keys(summaries).forEach((shoeOrderId) => {
		// 			summaryTranslations.push(
		// 				this.generateShoeOrderText(
		// 					summaries[shoeOrderId].name,
		// 					cartOrder.uid,
		// 					shoeOrderId,
		// 					summaries[shoeOrderId].summary,
		// 				),
		// 			)
		// 		})
		// 		return forkJoin(summaryTranslations)
		// 	}),
		// 	switchMap((translationArray) => {
		// 		// set translations and switch to check out observable
		// 		cartOrder.shoes.forEach((shoeOrder) => {
		// 			let index = translationArray.findIndex((t) => t.shoeIdentifier === shoeOrder.id)
		// 			shoeOrder.shoeName = translationArray[index].shoeName
		// 			shoeOrder.imageUrl = translationArray[index].imageUrl
		// 			shoeOrder.componentTableRows = translationArray[index].translations
		// 		})
		//
		//
		// 		if (publicShop) {
		// 			return this.stripeService
		// 				.checkout(cartOrder)
		// 				.pipe(mergeMap((res) => this.stripeService.redirectToCheckout(res.id)))
		// 		} else {
		// 			cartOrder.status = OrderStatus.New
		// 			return from(this.ordersService.add(cartOrder, { userUid: shopSettings.uid })).pipe(
		// 				tap(() => this.cartService.clearCart()),
		// 			)
		// 		}
		// 	}),
		// )
	}

	protected getErrorMessage(err: unknown, model: CartOrder): string {
		return `Error!`
	}

	protected getSuccessMessage(model: CartOrder): string {
		return `Success!`
	}

	private async confirmOrderByLoggedInUser(): Promise<boolean> {
		const dialogRef = this.dialog.open(SellerOrderConfirmationDialog, {
			width: '500px',
			disableClose: true,
		})
		const data = await firstValueFrom(dialogRef.afterClosed().pipe(take(1)))
		return !!(data && data?.status === 'success')
	}

	private createOrder(cartOrder: CartOrder): Order {
		const order = new Order()
		order.uid = cartOrder.uid
		order.status = OrderStatus.Draft
		order.items = cartOrder.items
		order.customer = cartOrder.customer
		order.phone = cartOrder.phone
		order.email = cartOrder.email
		order.address = cartOrder.address
		order.city = cartOrder.city
		order.county = cartOrder.county
		return order
	}

	/** @deprecated */
	private generateShoeOrderText(
		shoeDisplayName: string,
		orderID: string,
		shoeID: string,
		shoeComponentsSummary: Array<ShoeComponentTableItem>,
	): Observable<any> {
		let shoeName = ''
		return from(shoeComponentsSummary).pipe(
			switchMap((item) => {
				return forkJoin([of(item), this.translateService.get(shoeDisplayName)])
			}),
			switchMap(([item, translatedName]) => {
				shoeName = translatedName
				const translationsToFetch = []
				translationsToFetch.push(item.name)
				if (item.modelOption) {
					translationsToFetch.push(item.modelOption)
					item.material && translationsToFetch.push(item.material)
				} else {
					translationsToFetch.push(item.material ?? '')
					item.patina && translationsToFetch.push('material.patinat')
				}
				translationsToFetch.push(item.color ?? '')
				console.log('translationsToFetch', translationsToFetch)
				return this.translateService.get(translationsToFetch)
			}),
			map((translations: Record<string, string>) => {
				console.log('translations', translations)
				const translationValues = Object.values(translations).map((translation) => translation ?? '')
				const tableRow: ShoeComponentTableRow = {
					col1: '',
					col2: '',
					col3: '',
				}
				if (translationValues.length === 4) {
					tableRow.col1 = translationValues[0] ?? ''
					tableRow.col2 = translationValues[1] + ' - ' + (translationValues[2] ?? '')
					tableRow.col3 = translationValues[3] ?? ''
				} else if (translationValues.length === 3) {
					tableRow.col1 = translationValues[0] ?? ''
					tableRow.col2 = translationValues[1] ?? ''
					tableRow.col3 = translationValues[2] ?? ''
				}
				return tableRow
			}),
			bufferCount(shoeComponentsSummary.length),
			switchMap((translations) => {
				// @refactor - this shouldn't be here
				return forkJoin([
					of(translations),
					defer(() => getDownloadURL(ref(this.storage, `uploads/snapshots/${orderID}${shoeID ?? ''}1.png`))),
				])
			}),
			map(([translations, imageUrl]) => {
				return {
					shoeIdentifier: shoeID,
					shoeName: shoeName,
					imageUrl: imageUrl,
					translations: translations,
				}
			}),
		)
	}
}
